<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">
        Telegram Notification
      </span>
    </div>
    <div class="d-flex justify-content-center align-items-center pt-35-px" v-if="isLoading">
      <Spinner />
    </div>
    <transition name="slide-up">
      <template v-if="!isLoading">
        <div class="a-filter">
          <a-row class="full-w" type="flex" justify="start" :gutter="16">
            <a-col :span="5">
              <div class="full-w a-input-group" :class="{ active: !!filterParams.search }">
                <a-button size="large">
                  <a-icon type="search" />
                </a-button>
                <a-input class="custom-prefix" placeholder="Search" @change="onSearch"></a-input>
              </div>
            </a-col>
          </a-row>
        </div>
      </template>
    </transition>
    <div v-resize="elSizeChange" class="a-body flex-grow-1 overflow-y-hidden">
      <transition name="slide-up">
        <template v-if="!isLoading">
          <PaginationTable 
            :pagination="pagin" 
            :columns="TelegramNotificationColumn" 
            :dataSource="filterData" 
            :tableSize="tableSize"
            :rowClickable="true"
            @row-click="onRowClick"
          >
            <div slot="registrationCode" slot-scope="{ record }">
              <template v-if="record.registrationCode">
                <a-tooltip title="Click to copy">
                  <code class="registration-code" @click.stop="copyToClipboard(record.registrationCode)">
                    {{ record.registrationCode }}
                  </code>
                </a-tooltip>
              </template>
              <template v-else>
                <span class="not-generated">-</span>
              </template>
            </div>
            <div slot="telegramLink" slot-scope="{ record }">
              <template v-if="record.telegramLink">
                <a-tooltip :title="record.telegramLink">
                  <a :href="record.telegramLink" target="_blank" class="telegram-link" @click.stop>
                    {{ truncateLink(record.telegramLink) }}
                  </a>
                </a-tooltip>
              </template>
              <template v-else>
                <span class="not-generated">-</span>
              </template>
            </div>
            <div slot="totalSubscriptions" slot-scope="{ record }">
              <p>{{ record.totalSubscriptions }}</p>
            </div>
            <template slot="actions" slot-scope="{ record }">
              <div class="action-buttons" @click.stop>
                <a-button 
                  type="primary" 
                  size="small" 
                  :loading="record.isGenerating"
                  @click="onGenerateToken(record)"
                >
                  {{ record.telegramLink ? 'Regenerate' : 'Generate' }}
                </a-button>
                <a-button 
                  v-if="record.telegramLink"
                  type="danger" 
                  size="small"
                  :loading="record.isInvalidating"
                  @click="showInvalidateConfirm(record)"
                >
                  Invalidate
                </a-button>
              </div>
            </template>
          </PaginationTable>
        </template>
      </transition>
    </div>
    <div class="a-footer">
      <PaginationBar :pageSize.sync="pagin.pageSize" :total="pagin.total" @change="paginChange" />
    </div>

    <!-- Subscriptions Modal -->
    <a-modal
      v-model="subscriptionModal.visible"
      :title="subscriptionModal.title"
      :footer="null"
      :width="1000"
      :maskClosable="true"
      @cancel="closeSubscriptionModal"
    >
      <div v-if="subscriptionModal.loading" class="subscription-loading">
        <a-spin size="large" />
      </div>
      <div v-else>
        <div v-if="subscriptionModal.subscriptions.length === 0" class="no-subscriptions">
          <a-empty description="No subscriptions found for this contact group" />
        </div>
        <div v-else class="subscriptions-list">
          <div class="subscription-summary">
            <span>Total: {{ subscriptionModal.totalCount }} subscription(s)</span>
          </div>
          <a-table
            :columns="subscriptionColumns.filter(col => col.key !== 'severity' || isAlertTriageEnabled)"            :dataSource="subscriptionModal.subscriptions"
            :pagination="false"
            :rowKey="record => record.id"
            size="middle"
            bordered
          >
            <template slot="chatType" slot-scope="text">
              <p style="text-transform: capitalize;">{{ text }}</p>
            </template>
            <template slot="createdAt" slot-scope="text">
              {{ formatDate(text) }}
            </template>
            <template slot="severity" slot-scope="text, record" v-if="isAlertTriageEnabled">
              <div class="severity-checkboxes">
                <a-checkbox 
                  :checked="isSeverityChecked(record.triage_severity_selected, 'HIGH')"
                  @change="(e) => onSeverityChange(record, 'HIGH', e.target.checked)"
                  :disabled="record.isUpdatingSeverity"
                >
                  HIGH
                </a-checkbox>
                <a-checkbox 
                  :checked="isSeverityChecked(record.triage_severity_selected, 'MEDIUM')"
                  @change="(e) => onSeverityChange(record, 'MEDIUM', e.target.checked)"
                  :disabled="record.isUpdatingSeverity"
                >
                  MEDIUM
                </a-checkbox>
                <a-checkbox 
                  :checked="isSeverityChecked(record.triage_severity_selected, 'LOW')"
                  @change="(e) => onSeverityChange(record, 'LOW', e.target.checked)"
                  :disabled="record.isUpdatingSeverity"
                >
                  LOW
                </a-checkbox>
              </div>
            </template>
            <template slot="action" slot-scope="text, record">
              <a-popconfirm
                title="Are you sure you want to remove this subscription?"
                ok-text="Yes"
                cancel-text="No"
                @confirm="onDeleteSubscription(record)"
              >
                <a-button 
                  type="danger" 
                  size="small"
                  :loading="record.isDeleting"
                >
                  Remove
                </a-button>
              </a-popconfirm>
            </template>
          </a-table>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
  import { PAGE_OPTIONS } from '@constants/pageOptions';
  import { TelegramNotificationColumn } from '../columnsTable/TelegramNotificationColumn';
  import TelegramNotificationService from '../services/telegram-notification.service';
  import ContactGroupService from '@modules/ContactGroup/services/contact-group.service';

  export default {
    name: 'TelegramNotificationList',
    data() {
      var pageSize = Number(window.localStorage.getItem("pageSize"));
      if (!pageSize) pageSize = 5;

      return {
        TelegramNotificationColumn,
        data: [],
        filterData: [],
        filterParams: {
          search: '',
        },
        pagin: {
          current: 1,
          pageSize: pageSize,
          total: 0,
        },
        PAGE_OPTIONS,
        tableScrollHeight: 500,
        isLoading: true,
        tableSize: {
          height: '',
          width: '',
        },
        subscriptionModal: {
          visible: false,
          loading: false,
          title: 'Telegram Subscriptions',
          contactGroupId: null,
          subscriptions: [],
          totalCount: 0,
        },
        subscriptionColumns: [
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 180,
          },
          {
            title: 'Chat Type',
            dataIndex: 'chat_type',
            key: 'chat_type',
            width: 100,
            scopedSlots: { customRender: 'chatType' },
          },
          {
            title: 'Registered At',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            scopedSlots: { customRender: 'createdAt' },
          },
          {
            title: 'Severity to send alerts',
            dataIndex: 'severity',
            key: 'severity',
            width: 150,
            scopedSlots: { customRender: 'severity' },
          },
          {
            title: 'Action',
            key: 'action',
            width: 100,
            scopedSlots: { customRender: 'action' },
          },
        ],
      };
    },
    mounted() {
      this.getList();
    },
    methods: {
      filter() {
        const searchTerm = this.filterParams.search.trim().toLowerCase();
        this.filterData = searchTerm 
          ? this.data.filter(item => item.description.toLowerCase().includes(searchTerm)) 
          : this.data;
        this.pagin = {
          current: 1,
          pageSize: this.pagin.pageSize,
          total: this.filterData.length,
        };
      },
      onSearch(event) {
        this.filterParams.search = event.target.value;
        this.filter();
      },
      paginChange(pagin) {
        this.pagin = { ...pagin };
      },
      async getList() {
        try {
          const contactGroups = await ContactGroupService.getList();
          
          // Fetch telegram info for each contact group
          const dataWithTelegram = await Promise.all(
            (contactGroups || []).map(async (group, index) => {
              let telegramInfo = null;
              try {
                telegramInfo = await TelegramNotificationService.getTelegramInfo(group.id);
              } catch (error) {
                // If 404 or error, telegram not set up for this group
                telegramInfo = null;
              }
              
              // Extract registration code from the link
              const registrationCode = this.extractRegistrationCode(telegramInfo?.registration_link);
              
              return {
                ...group,
                index: index + 1,
                telegramLink: telegramInfo?.registration_link || null,
                registrationCode: registrationCode,
                totalSubscriptions: telegramInfo?.registered_users_count || 0,
                isGenerating: false,
                isInvalidating: false,
              };
            })
          );
          
          this.data = dataWithTelegram;
          this.filter();
        } catch {
          this.$showErrorMessage('HTTP error received from web server.');
        } finally {
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }
        }
      },
      extractRegistrationCode(link) {
        if (!link) return null;
        // Format: https://t.me/BotName?start=<64char_token>
        const match = link.match(/[?&]start=([a-zA-Z0-9]+)/);
        return match ? match[1] : null;
      },
      truncateLink(link) {
        if (!link) return '';
        if (link.length > 50) {
          return link.substring(0, 50) + '...';
        }
        return link;
      },
      async copyToClipboard(text) {
        try {
          await navigator.clipboard.writeText(text);
          this.$showSuccessMessage('Registration code copied to clipboard');
        } catch {
          this.$showErrorMessage('Failed to copy to clipboard');
        }
      },
      async onGenerateToken(record) {
        record.isGenerating = true;
        try {
          const result = await TelegramNotificationService.generateToken(record.id);
          record.telegramLink = result?.registration_link || null;
          record.registrationCode = this.extractRegistrationCode(record.telegramLink);
          this.$showSuccessMessage('Telegram registration link generated successfully');
        } catch {
          this.$showErrorMessage('Failed to generate Telegram registration link');
        } finally {
          record.isGenerating = false;
        }
      },
      async onInvalidateToken(record) {
        record.isInvalidating = true;
        try {
          await TelegramNotificationService.invalidateToken(record.id);
          record.telegramLink = null;
          record.registrationCode = null;
          this.$showSuccessMessage('Telegram registration link invalidated successfully');
        } catch {
          this.$showErrorMessage('Failed to invalidate Telegram registration link');
        } finally {
          record.isInvalidating = false;
        }
      },
      showInvalidateConfirm(record) {
        this.$confirm({
          title: 'Are you sure you want to invalidate this Telegram registration link?',
          content: 'This will revoke the current registration link for this contact group.',
          okText: 'Yes',
          okType: 'danger',
          cancelText: 'No',
          onOk: () => {
            this.onInvalidateToken(record);
          }
        });
      },
      async onRowClick(record) {
        this.subscriptionModal.visible = true;
        this.subscriptionModal.loading = true;
        this.subscriptionModal.title = `Telegram Subscriptions - ${record.description}`;
        this.subscriptionModal.contactGroupId = record.id;
        
        try {
          const response = await TelegramNotificationService.getSubscriptions(record.id);
          this.subscriptionModal.subscriptions = (response?.subscriptions || []).map(sub => ({
            ...sub,
            isDeleting: false,
          }));
          this.subscriptionModal.totalCount = response?.total_count || 0;
        } catch {
          this.$showErrorMessage('Failed to load subscriptions');
          this.subscriptionModal.subscriptions = [];
        } finally {
          this.subscriptionModal.loading = false;
        }
      },
      closeSubscriptionModal() {
        this.subscriptionModal.visible = false;
        this.subscriptionModal.subscriptions = [];
        this.subscriptionModal.contactGroupId = null;
        // Refresh telegram info
        this.getList();
      },
      async onDeleteSubscription(record) {
        record.isDeleting = true;
        try {
          await TelegramNotificationService.deleteSubscription(
            this.subscriptionModal.contactGroupId,
            record.id
          );
          // Remove from list
          this.subscriptionModal.subscriptions = this.subscriptionModal.subscriptions.filter(
            sub => sub.id !== record.id
          );
          this.subscriptionModal.totalCount--;
          this.$showSuccessMessage('Subscription removed successfully');
        } catch {
          this.$showErrorMessage('Failed to remove subscription');
        } finally {
          record.isDeleting = false;
        }
      },
      formatDate(dateString) {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      },
      elSizeChange(size) {
        this.tableSize = size;
      },
      isSeverityChecked(severityValue, level) {
        // severityValue is binary-like: 111, 110, 101, 100, 011, 010, 001, 000
        const str = String(severityValue ?? 111).padStart(3, '0');
        if (level === 'HIGH') return str[0] === '1';
        if (level === 'MEDIUM') return str[1] === '1';
        if (level === 'LOW') return str[2] === '1';
        return false;
      },
      calculateSeverityValue(currentValue, level, checked) {
        const str = String(currentValue ?? 111).padStart(3, '0').split('');
        const newBit = checked ? '1' : '0';
        if (level === 'HIGH') str[0] = newBit;
        if (level === 'MEDIUM') str[1] = newBit;
        if (level === 'LOW') str[2] = newBit;
        return parseInt(str.join(''), 10);
      },
      async onSeverityChange(record, level, checked) {
        const newValue = this.calculateSeverityValue(record.triage_severity_selected, level, checked);
        this.$set(record, 'isUpdatingSeverity', true);
        try {
          await TelegramNotificationService.updateSeverity(
            this.subscriptionModal.contactGroupId,
            record.id,
            newValue
          );
          record.triage_severity_selected = newValue;
          this.$showSuccessMessage('Severity updated successfully');
        } catch {
          this.$showErrorMessage('Failed to update severity');
        } finally {
          this.$set(record, 'isUpdatingSeverity', false);
        }
      },
    },
    computed: {
    featureAccess() {
      return (this.$store && this.$store.state && this.$store.state.user && this.$store.state.user.data && this.$store.state.user.data.feature_access) || {};
    },
    isAlertTriageEnabled() {
      return Boolean(this.featureAccess['alert-triage']);
      }
    },
  };
</script>

<style lang="scss" scoped>
  .telegram-link {
    color: #1890ff;
    word-break: break-all;
    max-width: 100%;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  .not-generated {
    color: #999;
    font-style: italic;
  }
  
  .action-buttons {
    display: flex;
    gap: 8px;
  }

  .registration-code {
    background-color: #f5f5f5;
    padding: 2px 8px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 12px;
    cursor: pointer;
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    
    &:hover {
      background-color: #e8e8e8;
    }
  }

  .subscription-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
  }

  .no-subscriptions {
    padding: 20px;
  }

  .subscriptions-list {
    .subscription-summary {
      margin-bottom: 16px;
      font-size: 14px;
      color: #666;
    }
  }

  .severity-checkboxes {
    display: flex;
    text-align: center;
    gap: 4px;
  }
</style>
